import { Autocomplete, Popper, PopperProps, TextField } from '@mui/material';
import React, { useMemo } from 'react';
import { useAppSelector } from '../../app/hooks';
import { selectTopLevelMenuItems } from '../../selectors/menu';
import { IssueReportItem } from '../../redux/features/issueReport/issueReport.props';
import {
  IssueItemOption,
  buildIssueItemOptions,
} from '../../utils/issueReportItems';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import { IBrandMenuOrderItem } from '../../types/brandMenu';
import { ISSUE_TOOLTIP_Z_INDEX } from './issueReportLayers';

interface IssueItemPickerProps {
  /**
   * The cart AS IT WAS WHEN THE REPORT WAS FILED, off the record's frozen context —
   * never live cart state.
   *
   * This is the whole reason the prop exists instead of the component reading the
   * store. The backlog surfaces classify a report cars later, and by then the live
   * cart belongs to somebody else's order; offering it would invite the agent to
   * attach an item that was never in the cart they are reporting on.
   */
  agentCart: IBrandMenuOrderItem[] | undefined;
  /** The current selection, or null. */
  value: IssueReportItem | null;
  onChange: (item: IssueReportItem | null) => void;
  disabled?: boolean;
}

/**
 * HITL-1623 — which item is this issue about?
 *
 * ## Why a searchable combobox and not a dropdown
 *
 * The list is the cart PLUS every top-level menu item, and `topLevelMenuItems` is
 * built across every category, so on a real brand it runs to hundreds of entries. A
 * plain `<select>` stops being usable long before that. (The flag panel's earlier note
 * about avoiding dropdowns was about the 29-value TYPE taxonomy, where a scrolling
 * list buried the most-used value — it does not transfer to a list the agent can type
 * into.)
 *
 * ## Never pre-filled
 *
 * Even when the cart holds exactly one item, this opens EMPTY. Pre-filling from cart
 * state is a guess about what the agent meant, and a wrong guess costs more than an
 * empty field because they have to notice it before they can correct it.
 *
 * That rule is about STATE, not about gestures. HITL-1655 opens this panel from a flag
 * icon on a specific cart row and pre-selects that row — which is not a guess, because
 * the agent pointed at it. The rule in one line: pre-fill from what the agent DID,
 * never from what the state happens to be. Anyone "fixing" one of these to match the
 * other breaks the other's intent.
 *
 * ## Always shown, deliberately
 *
 * No per-type gating. The control renders whatever type is selected, including
 * `Unnecessary Staff Intervention` — ~52% of reports, which will never use it. Gating
 * it per type was specified and then dropped: it meant maintaining a list of
 * item-relevant types, a show/hide rule, and an answer for what happens when the agent
 * changes type mid-flow, to save one unused control.
 */
/**
 * The popper is PORTALLED to `document.body`, so it shares a stacking context with the
 * flag panel while defaulting to `theme.zIndex.modal` (1300) — far below the panel's
 * 9999. It therefore opened BEHIND the panel and the agent saw an empty dropdown with
 * its options hidden under an opaque surface.
 *
 * Same trap `issueReportLayers` already documents for the panel's tooltips, same fix.
 * Applied through `PopperComponent` rather than `componentsProps.popper` because MUI
 * 5.5's Autocomplete only admits `clearIndicator` and `paper` there, and via inline
 * `style` rather than `sx` so it beats the `.MuiAutocomplete-popper` class rule.
 *
 * Declared at module scope: a component defined inside the render is a new type every
 * render, which would remount the popper and close the list on every keystroke.
 */
const IssueItemPickerPopper = (props: PopperProps) => (
  <Popper
    {...props}
    style={{ ...props.style, zIndex: ISSUE_TOOLTIP_Z_INDEX }}
    // The SECOND cost of portalling, and it is not cosmetic. The agent-interception
    // guards listen on `document` in the capture phase and match with
    // `target.closest(ISSUE_REPORT_SELECTOR)`. The panel carries that marker on its
    // root, but this list is not a DOM descendant of the panel — so a click on an
    // option matched nothing and raised "would you like to take over?". Naming an
    // item in a bug report was being recorded as INTERCEPTING the order.
    //
    // Reporting an issue is never an interception. `IssueBacklogPopover` is the
    // precedent: portalled too, and it carries both this marker and a z-index above
    // the panel. A portalled issue-report surface needs the pair, never one of them.
    data-issue-report="true"
  />
);

const IssueItemPicker = ({
  agentCart,
  value,
  onChange,
  disabled,
}: IssueItemPickerProps) => {
  const topLevelMenuItems = useAppSelector(selectTopLevelMenuItems);

  const options = useMemo(
    () => buildIssueItemOptions(agentCart, topLevelMenuItems),
    [agentCart, topLevelMenuItems]
  );

  // The value arrives as a plain `IssueReportItem` off the record, which is NOT
  // reference-equal to any option, so matching is by name — the same key the options
  // are de-duplicated on. Falling back to the raw value keeps a selection visible even
  // when its option is absent, which is what a report filed against a menu that has
  // since changed looks like.
  const selected = useMemo<IssueItemOption | IssueReportItem | null>(() => {
    if (!value) return null;
    return options.find((option) => option.name === value.name) ?? value;
  }, [options, value]);

  return (
    <Autocomplete
      size="small"
      disabled={disabled}
      options={options}
      value={selected as IssueItemOption | null}
      // Cart entries come first out of `buildIssueItemOptions`, and MUI groups
      // CONSECUTIVE equal values — so the array order is what produces the two
      // sections, and sorting the options would silently merge them.
      groupBy={(option) => option.group}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, picked) => option.name === picked.name}
      onChange={(_event, picked) => {
        if (!picked) {
          onChange(null);
          return;
        }
        // The `group` field is presentation only — strip it so what reaches the record
        // is exactly the wire shape and nothing has to remember to drop it later.
        const { name, from, menu_item_id, menu_category } = picked;
        onChange({ name, from, menu_item_id, menu_category });
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Item (optional)"
          inputProps={{
            ...params.inputProps,
            'aria-label': 'Item this issue is about',
            'data-testid': 'issue-item-picker',
          }}
        />
      )}
      sx={{
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'rgba(255,255,255,0.04)',
          color: '#fff',
          fontSize: HITL3_FONT_SIZE,
          borderRadius: '3px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(255,255,255,0.28)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(255,255,255,0.45)',
        },
        // Matches the detail field beside it: same green, same 1px. The two controls
        // share a row, so a focus treatment that differed in either colour or weight
        // would read as them being different KINDS of control rather than the same
        // control focused. 1px because MUI's focused default is 2px, which changes
        // the field's apparent size on focus and nudges the row.
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
          {
            borderColor: `${Colors.green} !important`,
            borderWidth: '1px !important',
          },
        // `opacity: 1` because MUI dims placeholders by default, which on this dark
        // ground drops the only label the control has below readable contrast.
        '& .MuiInputBase-input::placeholder': {
          color: 'rgba(255,255,255,0.45)',
          opacity: 1,
        },
        '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.7)' },
      }}
      PopperComponent={IssueItemPickerPopper}
      componentsProps={{
        paper: {
          sx: { '& .MuiAutocomplete-groupLabel': { fontSize: '0.62rem' } },
        },
      }}
    />
  );
};

export default IssueItemPicker;
